import { interpolateRgb, scaleSequential } from "d3";

import {
  BAD_COLOR,
  EXTRA_GOOD_COLOR,
  GOOD_COLOR,
  NA_COLOR,
  NEUTRAL_PALETTE,
  NEUTRAL_PALETTE_FOR_WATER,
  SsurgoDatatype,
  WARN_COLOR,
} from "./types";

// Fetch a color for a value x from a given numeric domain and color range
// as an on-the-fly scale (calculated once at startup time)
function colorForValue(domain: number[], range: string[], value: number) {
  const scale = scaleSequential(interpolateRgb(range[0], range[1])).domain(
    domain,
  );
  return scale(value);
}

export const SSURGO_DATATYPES: SsurgoDatatype[] = [
  {
    name: "Farmland Classification",
    id: "farmland_class",
    visualizationKey: "farmlndcl",
    mapboxPaintStyle: {
      "fill-color": [
        "match",
        ["get", "farmlndcl"],
        "All areas are prime farmland",
        GOOD_COLOR,
        BAD_COLOR, // not prime farmland
      ],
    },
    categorization: [
      {
        id: "prime_farmland",
        label: "Prime Farmland",
        valueMatch: (value) => value === "All areas are prime farmland",
        color: [GOOD_COLOR],
      },
      {
        id: "not_prime_farmland",
        label: "Not Prime Farmland",
        valueMatch: (value) => value !== "All areas are prime farmland",
        color: [BAD_COLOR],
      },
    ],
    histogramTitle:
      "Farmland Classification distribution across all fields (percent of acres)",
    description: (
      <>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
  },
  {
    name: "Taxonomic Classification",
    id: "taxonomic_class",
    visualizationKey: "taxclname",
    mapboxPaintStyle: {
      "fill-color": NA_COLOR,
      "fill-opacity": 0.6,
    },
  },
  {
    name: "Cation Exchange Capacity (CEC)",
    id: "cec7",
    visualizationKey: "cec7_r",
    // NOTE: CEC actually goes 0-400
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "cec7_r"],
        ...[0, BAD_COLOR, 15, WARN_COLOR, 40, GOOD_COLOR],
      ],
    },
    description: (
      <>
        <span className="font-bold">What is CEC?</span>
        <p className="pb-2">
          CEC is a useful indicator of soil fertility because it shows the
          soil&apos;s ability to supply three important plant nutrients:
          calcium, magnesium and potassium.
        </p>
        <p>
          The higher the CEC, the more nutrients the soil can hold, which can
          lead to better plant growth and higher crop yields. Organic matter has
          a very high CEC, and soils with a higher clay fraction tend to have a
          higher CEC.
        </p>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
    histogramTitle: "CEC distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "low_cec",
        label: "Low CEC",
        subLabel: "(<10 meq/100g)",
        valueMatch: (value: number) => value < 10,
        color: [BAD_COLOR, colorForValue([0, 15], [BAD_COLOR, WARN_COLOR], 10)],
      },
      {
        id: "mid_cec",
        label: "Mid CEC",
        subLabel: "(10-20 meq/100g)",
        valueMatch: (value) => value >= 10 && value < 20,
        color: [
          colorForValue([0, 15], [BAD_COLOR, WARN_COLOR], 10),
          // Stick warn color in here to help with the gradient looking muddy
          WARN_COLOR,
          colorForValue([15, 40], [WARN_COLOR, GOOD_COLOR], 20),
        ],
      },
      {
        id: "high_cec",
        label: "High CEC",
        subLabel: "(20-40 meq/100g)",
        valueMatch: (value) => value >= 20 && value < 40,
        color: [
          colorForValue([15, 40], [WARN_COLOR, GOOD_COLOR], 20),
          GOOD_COLOR,
        ],
      },
      {
        id: "very_high_cec",
        label: "Very High CEC",
        subLabel: "(>40 meq/100g)",
        valueMatch: (value) => value >= 40,
        color: [GOOD_COLOR],
      },
    ],
  },
  {
    name: "Available Water Capacity",
    id: "awc",
    visualizationKey: "awc_r",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "awc_r"],
        ...[0, BAD_COLOR, 0.1, WARN_COLOR, 0.15, WARN_COLOR, 0.2, GOOD_COLOR],
      ],
    },
    description: (
      <>
        <span className="font-bold">What is Available Water Capacity?</span>
        <p>
          Available Water Capacity is an indicator of soil quality. It&apos;s
          the maximum amount of plant available water a soil can provide. It can
          be improved through cover crop and crop rotation.
        </p>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
    histogramTitle:
      "Available Water Capacity distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "low_awc",
        label: "Low AWC",
        subLabel: "(<0.1 cm/cm)",
        valueMatch: (value) => value < 0.1,
        color: [BAD_COLOR, WARN_COLOR],
      },
      {
        id: "mid_awc",
        label: "Moderate AWC",
        subLabel: "(0.1-0.15 cm/cm)",
        valueMatch: (value) => value >= 0.1 && value < 0.15,
        color: [WARN_COLOR],
      },
      {
        id: "high_awc",
        label: "High AWC",
        subLabel: "(0.15-0.2 cm/cm)",
        valueMatch: (value) => value >= 0.15 && value < 0.2,
        color: [WARN_COLOR, GOOD_COLOR],
      },
      {
        id: "very_high_awc",
        label: "Very High AWC",
        subLabel: "(>0.2 cm/cm)",
        valueMatch: (value) => value >= 0.2,
        color: [GOOD_COLOR],
      },
    ],
  },
  {
    name: "Drainage Class",
    id: "drainage_class",
    visualizationKey: "drainagecl",
    mapboxPaintStyle: {
      "fill-color": [
        "match",
        ["get", "drainagecl"],
        ["Excessively drained", "Somewhat excessively drained"],
        EXTRA_GOOD_COLOR,
        ["Well drained", "Moderately well drained"],
        GOOD_COLOR,
        ["Somewhat poorly drained"],
        WARN_COLOR,
        ["Poorly drained", "Very poorly drained"],
        BAD_COLOR,
        NA_COLOR,
      ],
    },
    description: (
      <>
        <span className="font-bold">What is Drainage Class?</span>
        <p>
          The rate water drains into the soil affects the amount and timing of
          runoff, what crops can be grown, and where wetlands form. In soils
          with low drainage rates water will pond on the soil&apos;s surface.
        </p>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
    histogramTitle:
      "Drainage Class distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "excessively_drained",
        label: "Excessively Drained",
        valueMatch: (value) =>
          ["Excessively drained", "Somewhat excessively drained"].includes(
            value,
          ),
        color: [EXTRA_GOOD_COLOR],
      },
      {
        id: "well_drained",
        label: "Well Drained",
        valueMatch: (value) =>
          ["Well drained", "Moderately well drained"].includes(value),
        color: [GOOD_COLOR],
      },
      {
        id: "somewhat_poorly_drained",
        label: "Somewhat Poorly Drained",
        valueMatch: (value) => value === "Somewhat poorly drained",
        color: [WARN_COLOR],
      },
      {
        id: "poorly_drained",
        label: "Poorly Drained",
        valueMatch: (value) =>
          ["Poorly drained", "Very poorly drained"].includes(value),
        color: [BAD_COLOR],
      },
      {
        id: "data_unavailable",
        label: "Data Unavailable",
        valueMatch: (value) => value === "Data Unavailable",
        color: [NA_COLOR],
        hideIfZero: true,
      },
    ],
  },
  {
    name: "Flooding Frequency",
    id: "flood_frequency_dcd",
    visualizationKey: "flodfreqdcd",
    mapboxPaintStyle: {
      "fill-color": [
        "match",
        ["get", "flodfreqdcd"],
        ["None"],
        EXTRA_GOOD_COLOR,
        ["Very Rare", "Rare"],
        GOOD_COLOR,
        ["Occasional"],
        WARN_COLOR,
        BAD_COLOR, //["Frequent", "Very Frequent"],
      ],
    },
    histogramTitle:
      "Flooding Frequency distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "none",
        label: "None",
        valueMatch: (value) => ["None"].includes(value),
        color: [EXTRA_GOOD_COLOR],
      },
      {
        id: "rare",
        label: "Rare",
        valueMatch: (value) => ["Rare", "Very Rare"].includes(value),
        color: [GOOD_COLOR],
      },
      {
        id: "occasional",
        label: "Occasional",
        valueMatch: (value) => ["Occasional"].includes(value),
        color: [WARN_COLOR],
      },
      {
        id: "frequent",
        label: "Frequent",
        valueMatch: (value) => ["Frequent", "Very Frequent"].includes(value),
        color: [BAD_COLOR],
      },
    ],
    description: (
      <>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
  },
  {
    name: "Soil Erodibility Factor",
    id: "soil_erodability_factor",
    visualizationKey: "kffact",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["to-number", ["get", "kffact"]],
        0,
        GOOD_COLOR,
        0.25,
        WARN_COLOR,
        1,
        BAD_COLOR,
      ],
    },
    description: (
      <>
        <span className="font-bold">What is Soil Erodability Factor?</span>
        <p>
          Soil erodibility factor, or K Factor, quantifies the susceptibility of
          soil particles to detachment and movement by water.
        </p>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
    histogramTitle:
      "Soil Erodability Factor distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "high_erodability",
        label: "High Erodability",
        subLabel: "(>0.45)",
        valueMatch: (value) => value > 0.45,
        color: [
          BAD_COLOR,
          colorForValue([1, 0.25], [BAD_COLOR, WARN_COLOR], 0.45),
        ],
      },
      {
        id: "mid_erodability",
        label: "Mid Erodability",
        subLabel: "(0.2-0.45)",
        valueMatch: (value) => value > 0.2 && value <= 0.45,
        color: [
          colorForValue([1, 0.25], [BAD_COLOR, WARN_COLOR], 0.45),
          colorForValue([0.25, 0], [WARN_COLOR, GOOD_COLOR], 0.2),
        ],
      },
      {
        id: "low_erodability",
        label: "Low Erodability",
        subLabel: "(<0.2)",
        valueMatch: (value) => value <= 0.2,
        color: [
          colorForValue([0.25, 0], [WARN_COLOR, GOOD_COLOR], 0.2),
          GOOD_COLOR,
        ],
      },
    ],
  },
  {
    name: "Wind Erodibility Factor",
    id: "wind_erodability",
    visualizationKey: "weg",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["to-number", ["get", "weg"]],
        0,
        BAD_COLOR,
        4,
        WARN_COLOR,
        8,
        GOOD_COLOR,
      ],
    },
    histogramTitle:
      "Wind Erodability Factor distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "high_wind_erodability",
        label: "High Wind Erodability",
        subLabel: "(<3)",
        valueMatch: (value) => value <= 3,
        color: [BAD_COLOR, colorForValue([0, 4], [BAD_COLOR, WARN_COLOR], 3)],
      },
      {
        id: "mid_wind_erodability",
        label: "Mid Wind Erodability",
        subLabel: "(3-5)",
        valueMatch: (value) => value > 3 && value <= 5,
        color: [
          colorForValue([0, 4], [BAD_COLOR, WARN_COLOR], 3),
          colorForValue([4, 8], [WARN_COLOR, GOOD_COLOR], 5),
        ],
      },
      {
        id: "low_wind_erodability",
        label: "Low Wind Erodability",
        subLabel: "(>5)",
        valueMatch: (value) => value > 5,
        color: [colorForValue([4, 8], [WARN_COLOR, GOOD_COLOR], 5), GOOD_COLOR],
      },
    ],
    description: (
      <>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
  },
  {
    name: "Percent Clay",
    id: "clay_total",
    visualizationKey: "claytotal_r",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "claytotal_r"],
        ...[
          0,
          NEUTRAL_PALETTE[0],
          50,
          NEUTRAL_PALETTE[2],
          100,
          NEUTRAL_PALETTE[4],
        ],
      ],
    },
    histogramTitle:
      "Percent Clay distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "low_clay",
        label: "Low Clay Content",
        subLabel: "(<25%)",
        valueMatch: (value) => value < 25,
        color: [NEUTRAL_PALETTE[0], NEUTRAL_PALETTE[1]],
      },
      {
        id: "mid_clay",
        label: "Mid Clay Content",
        subLabel: "(25-75%)",
        valueMatch: (value) => value >= 25 && value < 75,
        color: [NEUTRAL_PALETTE[1], NEUTRAL_PALETTE[3]],
      },
      {
        id: "high_clay",
        label: "High Clay Content",
        subLabel: "(>75%)",
        valueMatch: (value) => value >= 75,
        color: [NEUTRAL_PALETTE[3], NEUTRAL_PALETTE[4]],
      },
    ],
    description: (
      <>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
  },
  {
    name: "Percent Sand",
    id: "sand_total",
    visualizationKey: "sandtotal_r",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "sandtotal_r"],
        ...[
          0,
          NEUTRAL_PALETTE[0],
          50,
          NEUTRAL_PALETTE[2],
          100,
          NEUTRAL_PALETTE[4],
        ],
      ],
    },
    histogramTitle:
      "Percent Sand distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "low_sand",
        label: "Low Sand Content",
        subLabel: "(<25%)",
        valueMatch: (value) => value < 25,
        color: [NEUTRAL_PALETTE[0], NEUTRAL_PALETTE[1]],
      },
      {
        id: "mid_sand",
        label: "Mid Sand Content",
        subLabel: "(25-75%)",
        valueMatch: (value) => value >= 25 && value < 75,
        color: [NEUTRAL_PALETTE[1], NEUTRAL_PALETTE[3]],
      },
      {
        id: "high_sand",
        label: "High Sand Content",
        subLabel: "(>75%)",
        valueMatch: (value) => value >= 75,
        color: [NEUTRAL_PALETTE[3], NEUTRAL_PALETTE[4]],
      },
    ],
    description: (
      <>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
  },
  {
    name: "Percent Silt",
    id: "silt_total",
    visualizationKey: "silttotal_r",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "silttotal_r"],
        ...[
          0,
          NEUTRAL_PALETTE[0],
          50,
          NEUTRAL_PALETTE[2],
          100,
          NEUTRAL_PALETTE[4],
        ],
      ],
    },
    histogramTitle:
      "Percent Silt distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "low_silt",
        label: "Low Silt Content",
        subLabel: "(<25%)",
        valueMatch: (value) => value < 25,
        color: [NEUTRAL_PALETTE[0], NEUTRAL_PALETTE[1]],
      },
      {
        id: "mid_silt",
        label: "Mid Silt Content",
        subLabel: "(25-75%)",
        valueMatch: (value) => value >= 25 && value < 75,
        color: [NEUTRAL_PALETTE[1], NEUTRAL_PALETTE[3]],
      },
      {
        id: "high_silt",
        label: "High Silt Content",
        subLabel: "(>75%)",
        valueMatch: (value) => value >= 75,
        color: [NEUTRAL_PALETTE[3], NEUTRAL_PALETTE[4]],
      },
    ],
    description: (
      <>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
  },
  {
    name: "Water Table Depth (Annual Min)",
    id: "water_table_depth_annual_min",
    visualizationKey: "wtdepannmin",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "wtdepannmin"],
        ...[
          0,
          NEUTRAL_PALETTE_FOR_WATER[4],
          100,
          NEUTRAL_PALETTE_FOR_WATER[2],
          200,
          NEUTRAL_PALETTE_FOR_WATER[0],
        ],
      ],
    },
    histogramTitle:
      "Water Table Depth (Annual Min) distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "shallow_water_table",
        label: "Shallow Water Table",
        subLabel: "(<50 cm)",
        valueMatch: (value) => value < 50,
        color: [NEUTRAL_PALETTE_FOR_WATER[4], NEUTRAL_PALETTE_FOR_WATER[3]],
      },
      {
        id: "mid_water_table",
        label: "Mid Water Table",
        subLabel: "(50-200 cm)",
        valueMatch: (value) => value >= 50 && value < 200,
        color: [NEUTRAL_PALETTE_FOR_WATER[3], NEUTRAL_PALETTE_FOR_WATER[0]],
      },
      {
        id: "deep_water_table",
        label: "Deep Water Table",
        subLabel: "(>200 cm)",
        valueMatch: (value) => value >= 200,
        color: [NEUTRAL_PALETTE_FOR_WATER[0]],
      },
    ],
    description: (
      <>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </>
    ),
  },
  {
    name: "Water Table Depth (Apr-Jun Min)",
    id: "water_table_depth_q2_min",
    visualizationKey: "wtdepaprjunmin",
    mapboxPaintStyle: {
      "fill-color": [
        "interpolate",
        ["linear"],
        ["get", "wtdepaprjunmin"],
        ...[
          0,
          NEUTRAL_PALETTE_FOR_WATER[4],
          100,
          NEUTRAL_PALETTE_FOR_WATER[2],
          200,
          NEUTRAL_PALETTE_FOR_WATER[0],
        ],
      ],
    },
    histogramTitle:
      "Water Table Depth (Apr-Jun Min) distribution across all fields (percent of acres)",
    categorization: [
      {
        id: "shallow_water_table",
        label: "Shallow Water Table",
        subLabel: "(<50 cm)",
        valueMatch: (value) => value < 50,
        color: [NEUTRAL_PALETTE_FOR_WATER[4], NEUTRAL_PALETTE_FOR_WATER[3]],
      },
      {
        id: "mid_water_table",
        label: "Mid Water Table",
        subLabel: "(50-200 cm)",
        valueMatch: (value) => value >= 50 && value < 200,
        color: [NEUTRAL_PALETTE_FOR_WATER[3], NEUTRAL_PALETTE_FOR_WATER[0]],
      },
      {
        id: "deep_water_table",
        label: "Deep Water Table",
        subLabel: "(>200 cm)",
        valueMatch: (value) => value >= 200,
        color: [NEUTRAL_PALETTE_FOR_WATER[0]],
      },
    ],
    description: (
      <div>
        <span className="font-bold">Where is this data from?</span>
        <p>
          Soil Survey Geographic Database (SSURGO), developed and maintained by
          the National Cooperative Soil Survey (NCSS) and USDA.
        </p>
      </div>
    ),
  },
];
